<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { groupReservationSlots } from '@/domain/reservations/reservation-slots';
  import type {
    IReservationSlot,
    IReservationSlots,
  } from '@/interfaces/IReservationSlots';
  import type { IReservationAlternative } from './reservation-form.interfaces';
  export interface IReservationSlotChoiceState {
    selectable: boolean;
    forced: boolean;
    canForce: boolean;
  }
  const props = withDefaults(
    defineProps<{
      value: string;
      payload: IReservationSlots | null;
      states: Record<string, IReservationSlotChoiceState>;
      loading?: boolean;
      failed?: boolean;
      disabled?: boolean;
      error?: string;
      conflict?: boolean;
      alternatives?: IReservationAlternative[];
    }>(),
    {
      loading: false,
      failed: false,
      disabled: false,
      error: undefined,
      conflict: false,
      alternatives: () => [],
    }
  );
  const emit = defineEmits<{
    (event: 'input', value: string): void;
    (event: 'force-request', slot: IReservationSlot): void;
    (event: 'retry'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const groups = computed(() =>
    props.payload ? groupReservationSlots(props.payload) : []
  );
  function state(slot: IReservationSlot): IReservationSlotChoiceState {
    return (
      props.states[slot.startsAt] ?? {
        selectable: slot.available,
        forced: false,
        canForce: false,
      }
    );
  }
  function reason(slot: IReservationSlot) {
    return slot.reason
      ? String(proxy.$t(`reservations.form.slotReason.${slot.reason}`))
      : '';
  }
  function choose(slot: IReservationSlot) {
    if (state(slot).selectable) emit('input', slot.startsAt);
    else if (state(slot).canForce) emit('force-request', slot);
  }
</script>
<template>
  <div class="reservation-slot-picker">
    <div v-if="conflict" class="conflict" role="alert">
      <strong>{{ $t('reservations.form.conflictTitle') }}</strong
      ><template v-if="alternatives.length"
        ><span>{{ $t('reservations.form.conflictAlternatives') }}</span>
        <div class="alternatives">
          <PosUiButton
            v-for="slot in alternatives"
            :key="slot.startsAt"
            color="neutral"
            variant="outline"
            :disabled="disabled || loading"
            @click="emit('input', slot.startsAt)"
            >{{ slot.label }}</PosUiButton
          >
        </div></template
      >
    </div>
    <div
      v-if="loading"
      class="slots-grid"
      aria-busy="true"
      :aria-label="$t('reservations.form.slotsLoading')"
    >
      <PosUiSkeleton v-for="index in 8" :key="index" class="slot-skeleton" />
    </div>
    <PosUiEmptyState
      v-else-if="failed"
      icon="cloud-alert"
      :title="$t('reservations.page.loadError')"
      ><template #action
        ><PosUiButton
          color="neutral"
          variant="outline"
          @click="emit('retry')"
          >{{ $t('reservations.page.retry') }}</PosUiButton
        ></template
      ></PosUiEmptyState
    >
    <PosUiEmptyState
      v-else-if="!payload || !payload.slots.length"
      icon="clock-outline"
      :title="
        $t(
          payload && payload.closed
            ? 'reservations.form.slotsClosed'
            : 'reservations.form.slotsEmpty'
        )
      "
    />
    <div v-else class="shift-groups">
      <div
        v-for="(group, index) in groups"
        :key="`${group.shift.id}:${index}`"
        class="shift-group"
      >
        <h4>
          {{ group.shift.name || $t('reservations.dates.customHours')
          }}<span
            >{{ group.shift.from.slice(0, 5) }}–{{
              group.shift.to.slice(0, 5)
            }}</span
          ><span>{{
            $t('reservations.common.covers', {
              count: group.shift.maxCovers
                ? `${group.shift.bookedCovers}/${group.shift.maxCovers}`
                : group.shift.bookedCovers,
            })
          }}</span>
        </h4>
        <div class="slots-grid">
          <button
            v-for="slot in group.slots"
            :key="slot.startsAt"
            class="slot-choice"
            type="button"
            :class="{
              selected: value === slot.startsAt,
              unavailable: !state(slot).selectable,
              forced: state(slot).forced,
            }"
            :disabled="
              disabled || (!state(slot).selectable && !state(slot).canForce)
            "
            :aria-pressed="value === slot.startsAt"
            :title="reason(slot)"
            @click="choose(slot)"
          >
            <span>{{ slot.label }}</span
            ><small v-if="slot.pacing">{{
              $t('reservations.form.pacing', slot.pacing)
            }}</small>
          </button>
        </div>
      </div>
    </div>
    <p v-if="error && !conflict" class="slot-error" role="alert">{{ error }}</p>
  </div>
</template>
<style scoped lang="scss">
  .reservation-slot-picker {
    @apply tw-flex tw-flex-col tw-gap-3;
    .shift-groups {
      @apply tw-flex tw-flex-col tw-gap-5;
    }
    .shift-group {
      @apply tw-flex tw-flex-col tw-gap-2;
      h4 {
        @apply tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-sm tw-font-semibold tw-text-neutral-800;
        span {
          @apply tw-text-xs tw-font-normal tw-tabular-nums tw-text-neutral-500;
        }
      }
    }
    .slots-grid {
      @apply tw-grid tw-gap-2;
      grid-template-columns: repeat(auto-fill, minmax(68px, 1fr));
    }
    .slot-choice {
      @apply tw-flex tw-min-h-12 tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2 tw-py-2 tw-text-sm tw-font-medium tw-tabular-nums tw-text-neutral-800;
      small {
        @apply tw-text-xs tw-font-normal;
      }
      &.unavailable {
        @apply tw-text-neutral-400;
        > span {
          @apply tw-line-through;
        }
      }
      &.selected {
        @apply tw-border-neutral-900 tw-bg-neutral-900 tw-text-white;
      }
      &.forced {
        @apply tw-border-amber-500 tw-bg-amber-50 tw-text-amber-800;
      }
      &:disabled {
        @apply tw-cursor-not-allowed tw-opacity-50;
      }
    }
    .slot-skeleton {
      @apply tw-h-12;
    }
    .conflict {
      @apply tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-red-200 tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-700;
    }
    .alternatives {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    .slot-error {
      @apply tw-m-0 tw-text-xs tw-text-red-600;
    }
  }
</style>
